import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useActiveChat } from "~/entities/chat/useActiveChat";
import { useProjectById } from "~/entities/project";
import { useScenesByProject } from "~/entities/scene";
import { useCurrentUser } from "~/entities/user";
import { displayWorkspaceName, useActiveWorkspace } from "~/entities/workspace";
import { useActiveProjectId } from "~/shared/store/useActiveProjectStore";
import { canWriteChat } from "~/features/chat-actions";

// Деривация данных активного чата для рендера: готовые пропсы ChatHeader
// (breadcrumb workspace / project / scene + title / pinned) и право записи в
// чат. Чистое чтение, без side-effects — навигация/синк живут в
// useChatRouteGuards. scrollToMessageRef добавляет ChatShell при рендере.
export function useChatShellContext(chatId: string | undefined) {
    const { t: tWs } = useTranslation("workspace");

    const chatQuery = useActiveChat(chatId);
    const { activeWorkspace, activeWorkspaceId } = useActiveWorkspace();
    const workspaceId = activeWorkspaceId ?? undefined;
    const workspaceName = activeWorkspace ? displayWorkspaceName(activeWorkspace, tWs) : undefined;
    const activeProjectId = useActiveProjectId(workspaceId);
    const projectQuery = useProjectById(activeProjectId ?? undefined);
    // Имя сцены тянем через listing проекта (отдельного GET /scenes/:id
    // нет). Список обычно уже закэширован страницей проекта; если нет —
    // первый рендер крошек будет без scene-сегмента, потом подтянется.
    const chatSceneId = chatQuery.data?.sceneId ?? null;
    const chatProjectId = chatQuery.data?.projectId ?? null;
    // Managers can open and read someone else's chat, but writes are reserved for
    // the owner or a superadmin. Keep both the composer and action menus aligned
    // with the backend write gate.
    const { data: currentUser } = useCurrentUser();
    const chatOwnerId = chatQuery.data?.userId ?? null;
    const scenesQuery = useScenesByProject(chatProjectId ?? undefined, { includeArchived: true });
    const sceneName = useMemo(
        () => scenesQuery.data?.data.find((s) => s.id === chatSceneId)?.name,
        [scenesQuery.data, chatSceneId],
    );

    // Разрешено ли писать (мутировать) активный чат: только владелец или
    // суперадмин. Fail-closed — пока владелец/текущий пользователь неизвестны,
    // composer/actions disabled (backend всё равно отклонит чужую запись с 403).
    const canWrite = canWriteChat(chatOwnerId, currentUser);

    const headerProps = {
        workspaceName,
        workspaceId: chatQuery.data?.workspaceId ?? null,
        chatTitle: chatQuery.data?.title,
        projectName: projectQuery.data?.name,
        projectId: chatProjectId,
        sceneName,
        sceneId: chatSceneId,
        onExport: undefined,
        exportDisabled: true,
        chatId: chatId ?? null,
        pinnedAt: chatQuery.data?.pinnedAt ?? null,
        canWrite,
    };

    return { headerProps, canWrite };
}
